import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, UserPlus, ClipboardList, Users, Mail, CheckCircle2, ArrowRight } from "lucide-react";

export const STATUS_META = {
  submitted: { label: "Submitted", cls: "bg-paw-blue-50 text-paw-primary" },
  invited: { label: "Invited", cls: "bg-amber-100 text-amber-700" },
  active: { label: "Active", cls: "bg-emerald-100 text-emerald-700" },
  in_treatment: { label: "In treatment", cls: "bg-violet-100 text-violet-700" },
  fulfilled: { label: "Fulfilled", cls: "bg-emerald-100 text-emerald-700" },
  cancelled: { label: "Cancelled", cls: "bg-paw-cream-2 text-paw-text-muted" },
};

export default function PartnerDashboard() {
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [me, refs] = await Promise.all([api.get("/partner/me"), api.get("/partner/referrals")]);
        setData(me.data);
        setRecent((refs.data.referrals || []).slice(0, 6));
      } catch { /* handled by layout gate */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="w-7 h-7 text-paw-primary/40 animate-spin" /></div>;

  const stats = data?.stats || { total: 0, by_status: {} };
  const invited = stats.by_status?.invited || 0;
  const active = (stats.by_status?.active || 0) + (stats.by_status?.in_treatment || 0);
  const fulfilled = stats.by_status?.fulfilled || 0;

  const CARDS = [
    { label: "Total referrals", value: stats.total, icon: Users },
    { label: "Awaiting signup", value: invited, icon: Mail },
    { label: "Active members", value: active, icon: CheckCircle2 },
    { label: "Fulfilled", value: fulfilled, icon: ClipboardList },
  ];

  return (
    <div className="max-w-[1080px] mx-auto px-5 sm:px-8 py-8 sm:py-10">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-[13px] font-extrabold tracking-[0.08em] uppercase text-paw-primary">{data?.partner?.org_name}</p>
          <h1 className="mt-1 text-2xl sm:text-3xl font-extrabold tracking-tight text-paw-navy">Welcome back, {data?.contact?.first_name || "partner"}</h1>
        </div>
        <Link to="/partner/submit" className="inline-flex items-center justify-center gap-2 bg-paw-primary hover:bg-paw-primary-hover text-white rounded-full px-6 py-3 font-semibold text-sm transition-all shadow-[0_8px_20px_-8px_rgba(15,98,214,0.6)]">
          <UserPlus className="w-4 h-4" /> Submit a referral
        </Link>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {CARDS.map((c) => (
          <div key={c.label} className="bg-white border border-paw-border rounded-2xl p-5 shadow-sm">
            <div className="w-10 h-10 rounded-xl grid place-items-center bg-paw-blue-50 text-paw-primary mb-3"><c.icon className="w-5 h-5" /></div>
            <div className="text-3xl font-extrabold tracking-tight text-paw-navy">{c.value}</div>
            <div className="text-sm text-paw-text-secondary mt-0.5">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 bg-white border border-paw-border rounded-3xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-paw-border">
          <h2 className="font-bold text-paw-navy">Recent referrals</h2>
          <Link to="/partner/referrals" className="text-sm font-semibold text-paw-primary inline-flex items-center gap-1">View all <ArrowRight className="w-4 h-4" /></Link>
        </div>
        {recent.length === 0 ? (
          <div className="px-6 py-14 text-center">
            <p className="text-paw-text-secondary">No referrals yet.</p>
            <Link to="/partner/submit" className="mt-4 inline-flex items-center gap-2 text-paw-primary font-semibold text-sm">Submit your first referral <ArrowRight className="w-4 h-4" /></Link>
          </div>
        ) : (
          <div className="divide-y divide-paw-border">
            {recent.map((r) => {
              const m = STATUS_META[r.status] || STATUS_META.submitted;
              return (
                <div key={r.referral_id} className="px-6 py-3.5 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-semibold text-paw-navy truncate">{r.member_first_name} {r.member_last_name}</p>
                    <p className="text-sm text-paw-text-muted truncate">{r.member_email}{r.pet_name ? ` · ${r.pet_name}` : ""}</p>
                  </div>
                  <span className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full ${m.cls}`}>{m.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
